import { useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { smtp, type ConfiguracaoSmtp as Configuracao } from '../api/acesso'
import { ErroApi } from '../api/client'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Campo } from '../componentes/Campo'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import './usuarios.css'

const SEGURANCAS = ['Automatica', 'StartTls', 'TlsImplicito', 'Nenhuma'] as const

const VAZIA: Configuracao = {
  servidor: '',
  porta: 587,
  seguranca: 'Automatica',
  usuario: '',
  remetenteEmail: '',
  remetenteNome: 'Portal Rio DG',
  urlBasePortal: '',
  senhaDefinida: false,
}

/**
 * Configuração do servidor de e-mail.
 *
 * Duas coisas passam por aqui: o código do segundo fator e o link de
 * recuperação de senha. Quer dizer que uma configuração errada não degrada o
 * portal — ela tranca todo mundo do lado de fora. Daí o botão de testar, que
 * valida o que está no formulário ANTES de virar o que está salvo.
 */
export function ConfiguracaoSmtp() {
  const { t } = useTranslation()

  const [cfg, setCfg] = useState<Configuracao | null>(null)
  const [senha, setSenha] = useState('')
  const [destinatario, setDestinatario] = useState('')
  const [falhou, setFalhou] = useState(false)
  const [recado, setRecado] = useState<{ tom: 'sucesso' | 'perigo' | 'info'; texto: string } | null>(null)
  const [enviando, setEnviando] = useState(false)
  const [testando, setTestando] = useState(false)

  useEffect(() => {
    smtp
      .ler()
      .then((lida) => setCfg({ ...VAZIA, ...lida }))
      .catch(() => setFalhou(true))
  }, [])

  if (falhou) return <Estado tipo="erro" />
  if (!cfg) return <Estado tipo="carregando" />

  const alterar = (campo: keyof Configuracao, valor: string | number) =>
    setCfg({ ...cfg, [campo]: valor })

  // Senha vazia significa "mantém a que está" — a tela nunca recebeu a senha
  // atual, então enviá-la vazia não pode apagá-la.
  const paraEnvio = { ...cfg, senha: senha || null }

  async function salvar(evento: FormEvent) {
    evento.preventDefault()
    setRecado(null)
    setEnviando(true)

    try {
      await smtp.gravar(paraEnvio)
      setSenha('')
      setCfg({ ...cfg!, senhaDefinida: cfg!.senhaDefinida || !!senha })
      setRecado({ tom: 'sucesso', texto: t('smtp.salvo') })
    } catch (e) {
      const corpo = e instanceof ErroApi ? (e.corpo as { detail?: string } | null) : null
      setRecado({ tom: 'perigo', texto: corpo?.detail ?? t('comum.erroDescricao') })
    } finally {
      setEnviando(false)
    }
  }

  async function testar() {
    setRecado(null)
    setTestando(true)

    try {
      const r = await smtp.testar(paraEnvio, destinatario)

      setRecado(
        r.sucesso
          ? { tom: 'sucesso', texto: t('smtp.testeOk', { destinatario }) }
          // A mensagem do servidor de e-mail aparece inteira: "falha ao
          // enviar" sem o motivo transforma o diagnóstico em adivinhação.
          : { tom: 'perigo', texto: t('smtp.testeFalhou', { erro: r.erro ?? '' }) },
      )
    } catch {
      setRecado({ tom: 'perigo', texto: t('comum.erroDescricao') })
    } finally {
      setTestando(false)
    }
  }

  return (
    <main className="usuarios">
      <header className="usuarios__cabecalho">
        <div>
          <h1 className="usuarios__titulo">{t('smtp.titulo')}</h1>
          <p className="usuarios__descricao">{t('smtp.descricao')}</p>
        </div>
      </header>

      {recado && <Aviso tom={recado.tom}>{recado.texto}</Aviso>}

      <Cartao titulo={t('smtp.servidorTitulo')}>
        <form onSubmit={salvar} noValidate>
          <Campo
            rotulo={t('smtp.servidor')}
            value={cfg.servidor}
            onChange={(e) => alterar('servidor', e.target.value)}
            obrigatorio
          />

          <Campo
            rotulo={t('smtp.porta')}
            type="number"
            value={cfg.porta}
            onChange={(e) => alterar('porta', Number(e.target.value))}
            obrigatorio
          />

          <fieldset className="usuarios__grupo">
            <legend>{t('smtp.seguranca')}</legend>
            {SEGURANCAS.map((s) => (
              <label key={s} className="usuarios__opcao">
                <input
                  type="radio"
                  name="seguranca"
                  value={s}
                  checked={cfg.seguranca === s}
                  onChange={() => alterar('seguranca', s)}
                />
                <span>
                  <strong>{t(`smtp.seg${s}`)}</strong>
                  <small>{t(`smtp.seg${s}Ajuda`)}</small>
                </span>
              </label>
            ))}
          </fieldset>

          <Campo
            rotulo={t('smtp.usuario')}
            value={cfg.usuario ?? ''}
            onChange={(e) => alterar('usuario', e.target.value)}
            autoComplete="off"
          />

          <Campo
            rotulo={t('smtp.senha')}
            type="password"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            autoComplete="new-password"
            // Não devolvemos a senha gravada, nem mascarada com o tamanho
            // real — o número de asteriscos já é informação.
            ajuda={cfg.senhaDefinida ? t('smtp.senhaGravada') : t('smtp.senhaAusente')}
          />

          <Campo
            rotulo={t('smtp.remetenteEmail')}
            type="email"
            value={cfg.remetenteEmail}
            onChange={(e) => alterar('remetenteEmail', e.target.value)}
            ajuda={t('smtp.remetenteAjuda')}
            obrigatorio
          />

          <Campo
            rotulo={t('smtp.remetenteNome')}
            value={cfg.remetenteNome}
            onChange={(e) => alterar('remetenteNome', e.target.value)}
            obrigatorio
          />

          <Campo
            rotulo={t('smtp.urlBase')}
            value={cfg.urlBasePortal}
            onChange={(e) => alterar('urlBasePortal', e.target.value)}
            ajuda={t('smtp.urlBaseAjuda')}
            obrigatorio
          />

          <div className="usuarios__acoes-formulario">
            <Botao type="submit" variante="primario" carregando={enviando}>
              {t('comum.salvar')}
            </Botao>
          </div>
        </form>
      </Cartao>

      <Cartao titulo={t('smtp.testeTitulo')}>
        <p className="usuarios__descricao">{t('smtp.testeDescricao')}</p>

        <Campo
          rotulo={t('smtp.testeDestinatario')}
          type="email"
          value={destinatario}
          onChange={(e) => setDestinatario(e.target.value)}
        />

        <div className="usuarios__acoes-formulario">
          <Botao carregando={testando} disabled={!destinatario} onClick={testar}>
            {t('smtp.testar')}
          </Botao>
        </div>
      </Cartao>
    </main>
  )
}
